import { formatDate } from '@/lib/utils';

export interface RequirementDisplayFields {
  requirement_number?: string | null;
  work_date?: string | null;
  work_end_date?: string | null;
  work_location?: string | null;
  activity_description?: string | null;
  priority?: string | null;
  labour_item_count?: number | null;
}

const PRIORITY_LABELS: Record<string, string> = {
  low: 'Low Priority',
  normal: 'Normal Priority',
  high: 'High Priority',
  urgent: 'Urgent Priority',
};

function clean(value: string | null | undefined): string {
  const t = (value ?? '').trim();
  if (!t || t.toLowerCase() === 'undefined' || t.toLowerCase() === 'null') return '';
  return t;
}

function dayMonth(date: string): string {
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('en-IN', { day: '2-digit', month: 'short' }).format(d);
}

/**
 * Date range in the app's en-IN format, e.g. "01 Aug 2026–07 Aug 2026". When both
 * dates share a year the range is compacted to the selector convention
 * "01 Aug–07 Aug 2026".
 */
export function formatRequirementRange(start?: string | null, end?: string | null): string {
  const s = clean(start);
  const e = clean(end);
  const ds = s ? new Date(s) : null;
  const de = e ? new Date(e) : null;
  const sd = ds && !isNaN(ds.getTime()) ? ds : null;
  const ed = de && !isNaN(de.getTime()) ? de : null;
  if (!sd && !ed) return '';
  if (sd && ed) {
    if (sd.getFullYear() === ed.getFullYear()) {
      const startPart = dayMonth(s);
      if (startPart) return `${startPart}–${formatDate(e)}`;
    }
    return `${formatDate(s)}–${formatDate(e)}`;
  }
  if (sd) return formatDate(s);
  return formatDate(e);
}

export function priorityLabel(priority?: string | null): string {
  const p = clean(priority).toLowerCase();
  if (!p) return '';
  return PRIORITY_LABELS[p] ?? clean(priority);
}

/**
 * Primary line. Priority: Work Location + Activity Description, Activity
 * Description, Work Location, date range, then the reference number.
 */
export function requirementPrimaryLabel(r: RequirementDisplayFields): string {
  const location = clean(r.work_location);
  const desc = clean(r.activity_description);
  if (location && desc) return `${location} – ${desc}`;
  if (desc) return desc;
  if (location) return location;
  const range = formatRequirementRange(r.work_date, r.work_end_date);
  if (range) return range;
  return clean(r.requirement_number);
}

/** Secondary line parts: `Start–End • Priority • categories • Requirement ID`. */
export function requirementSecondaryParts(r: RequirementDisplayFields): string[] {
  const parts: string[] = [];
  const range = formatRequirementRange(r.work_date, r.work_end_date);
  if (range) parts.push(range);
  const priority = priorityLabel(r.priority);
  if (priority) parts.push(priority);
  if (r.labour_item_count && r.labour_item_count > 0) {
    parts.push(`${r.labour_item_count} ${r.labour_item_count === 1 ? 'category' : 'categories'}`);
  }
  const id = clean(r.requirement_number);
  if (id) parts.push(id);
  return parts;
}

export function requirementSecondaryLabel(r: RequirementDisplayFields): string {
  return requirementSecondaryParts(r).join(' • ');
}

/** Everything the search box should match against (ID, dates, location, description, priority). */
export function requirementSearchText(r: RequirementDisplayFields): string {
  return [
    requirementPrimaryLabel(r),
    requirementSecondaryLabel(r),
    clean(r.work_location),
    clean(r.activity_description),
    priorityLabel(r.priority),
    clean(r.work_date),
    clean(r.work_end_date),
    clean(r.requirement_number),
  ]
    .filter(Boolean)
    .join(' ');
}
